import {signalStore, withState, withMethods, patchState, withComputed} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {pipe, switchMap, tap, catchError, of} from 'rxjs';
import {inject} from '@angular/core';
import {LookupVehicleService} from '../services/lookup-vehicle.service';
import {LookupSizeService} from '../services/lookup-size.service';
import {IVehicleFitment} from '../../types/IVehicleFitment';
import {IBrand} from '../../types/IBrand';
import {IAlternateVehicleFitment} from '../../types/IAlternateVehicleFitment';
import {setError, setFulfilled, setIdle, setPending, withRequestStatus} from './features/with-request-status';

type LookupState = {
  size: {
    widths: string[] | null,
    ratios: string[] | null,
    diameters: string[] | null,
    selected_category: 'retail' | 'commercial' | null,
    selected_width: string | null,
    selected_ratio: string | null,
    selected_diameter: string | null,
  },

  vehicle: {
    makes: string[] | null,
    models: string[] | null,
    submodels: string[] | null,
    fitments: IVehicleFitment[] | null,
    selected_year: string | null,
    selected_make: string | null,
    selected_model: string | null,
    selected_submodel: string | null,
    selected_fitment: IVehicleFitment | null,
    selected_rawsize: string | null,
    alternates: IAlternateVehicleFitment[] | null,
  },
};

const initialVehicleState = {
  makes: null,
  models: null,
  fitments: null,
  selected_make: null,
  selected_model: null,
  selected_fitment: null,
  selected_rawsize: null,
  selected_submodel: null,
  selected_year: null,
  submodels: null,
  alternates: null,
};

const initialSizeState = {
  diameters: null,
  ratios: null,
  selected_category: null,
  selected_diameter: null,
  selected_ratio: null,
  selected_width: null,
  widths: null
};

export const LookupStore = signalStore(
  {providedIn: 'root'},
  withState<LookupState>({
    size: initialSizeState,
    vehicle: initialVehicleState
  }),
  // Vehicle and size lookups run independently (both hydrate on load), so each has its own status
  withRequestStatus('vehicle'),
  withRequestStatus('size'),
  withRequestStatus('alternates'),
  withComputed((state) => ({})),
  withMethods((store, lookupVehicleService = inject(LookupVehicleService), lookupSizeService = inject(LookupSizeService)) => {
    // Aspect ratios are sometimes returned as a decimal fraction (e.g. "0.5") rather than digits ("50").
    const ratioDigits = (ratio: string | number): string => {
      const str = String(ratio);
      return str.includes('.') ? String(parseFloat(str) * 100) : str;
    };

    // Picks the longest candidate that the remaining rawsize digits start with, since widths/ratios can be 1-3 digits.
    // The API can return these as numbers rather than strings, so every candidate is coerced before comparing.
    const findLongestPrefixMatch = (
      candidates: Array<string | number>,
      digits: string,
      toDigits: (candidate: string | number) => string = (c) => String(c)
    ): string | null => {
      const sorted = [...candidates].sort((a, b) => toDigits(b).length - toDigits(a).length);
      const match = sorted.find((candidate) => digits.startsWith(toDigits(candidate)));
      return match === undefined ? null : String(match);
    };

    const getPackageValue = (fitment: IVehicleFitment): string => {
      if (fitment.rear_tire_size && fitment.front_tire_size !== fitment.rear_tire_size) {
        return `${fitment.front_tire_size}|${fitment.rear_tire_size}`;
      }
      return fitment.front_tire_size;
    };

    // Fetches fitments, patches state, and auto-selects the fitment if only one is returned.
    const fetchFitmentsStep$ = (year: string, make: string, model: string, submodel: string) =>
      lookupVehicleService.getFitments(year, make, model, submodel).pipe(
        tap((fitments: IVehicleFitment[]) => {
          const autoFitment = fitments.length === 1 ? fitments[0] : null;
          const autoSize = autoFitment?.front_tire_rawsize ?? null;
          patchState(store, setFulfilled('vehicle'), {
            vehicle: {...store.vehicle(), fitments, selected_fitment: autoFitment, selected_rawsize: autoSize}
          });
        })
      );

    // Fetches submodels, patches state, and cascades into fitments if only one submodel is returned.
    const fetchSubmodelsStep$ = (year: string, make: string, model: string) =>
      lookupVehicleService.getSubmodels(year, make, model).pipe(
        tap((submodels: string[]) => patchState(store, setFulfilled('vehicle'), {vehicle: {...store.vehicle(), submodels}})),
        switchMap((submodels: string[]) => {
          if (submodels.length !== 1) return of(submodels);
          patchState(store, setPending('vehicle'), {
            vehicle: {...store.vehicle(), selected_submodel: submodels[0], fitments: null, selected_fitment: null}
          });
          return fetchFitmentsStep$(year, make, model, submodels[0]);
        })
      );

    const fetchAlternatesStep$ = (chassisId: number) =>
      lookupVehicleService.getAlternateFitments(chassisId).pipe(
        tap((alternates: IAlternateVehicleFitment[]) => {
          patchState(store, setFulfilled('alternates'), { vehicle: { ...store.vehicle(), alternates } });
        }),
        catchError((error) => {
          patchState(store, setError('alternates', error.message));
          return of([]);
        })
      );

    return {
    loadAlternates: (chassisId: number) => {
      patchState(store, setPending('alternates'), { vehicle: { ...store.vehicle(), alternates: null } });
      fetchAlternatesStep$(chassisId).subscribe();
    },

    setYear: rxMethod<string>(
      pipe(
        tap((year: string) => {
          patchState(store, setPending('vehicle'), {vehicle: { ...initialVehicleState, selected_year: year }});
        }),
        switchMap((year) =>
          lookupVehicleService.getMakes(year).pipe(
            tap((makes: string[]) => {
              patchState(store, setFulfilled('vehicle'), {vehicle: {...store.vehicle(), makes}});
            }),
            catchError((error) => {
              patchState(store, setError('vehicle', error.message));
              return of([]);
            })
          )
        )
      )
    ),

    setMake: rxMethod<string>(
      pipe(
        tap((make: string) => {
          patchState(store, setPending('vehicle'), {
            vehicle: {
              ...store.vehicle(),
              selected_make: make,
              models: null,
              selected_model: null,
              submodels: null,
              selected_submodel: null,
              fitments: null,
              selected_fitment: null
            }
          });
        }),
        switchMap((make) => {
          const year = store.vehicle.selected_year()!;
          return lookupVehicleService.getModels(year, make).pipe(
            tap((models: string[]) => patchState(store, setFulfilled('vehicle'), {vehicle: {...store.vehicle(), models}})),
            switchMap((models: string[]) => {
              if (models.length !== 1) return of(models);
              patchState(store, setPending('vehicle'), {
                vehicle: {...store.vehicle(), selected_model: models[0], submodels: null, selected_submodel: null, fitments: null, selected_fitment: null}
              });
              return fetchSubmodelsStep$(year, make, models[0]);
            }),
            catchError((error) => {
              patchState(store, setError('vehicle', error.message));
              return of([]);
            })
          );
        })
      )
    ),

    setModel: rxMethod<string>(
      pipe(
        tap((model: string) => {
          patchState(store, setPending('vehicle'), {
            vehicle: {
              ...store.vehicle(),
              selected_model: model,
              submodels: null,
              selected_submodel: null,
              fitments: null,
              selected_fitment: null
            }
          });
        }),
        switchMap((model) => {
          const year = store.vehicle.selected_year()!;
          const make = store.vehicle.selected_make()!;
          return fetchSubmodelsStep$(year, make, model).pipe(
            catchError((error) => {
              patchState(store, setError('vehicle', error.message));
              return of([]);
            })
          );
        })
      )
    ),

    setSubmodel: rxMethod<string>(
      pipe(
        tap((submodel: string) => {
          patchState(store, setPending('vehicle'), {
            vehicle: {
              ...store.vehicle(),
              selected_submodel: submodel,
              fitments: null,
              selected_fitment: null
            }
          });
        }),
        switchMap((submodel) => {
          const year = store.vehicle.selected_year()!;
          const make = store.vehicle.selected_make()!;
          const model = store.vehicle.selected_model()!;
          return fetchFitmentsStep$(year, make, model, submodel).pipe(
            catchError((error) => {
              patchState(store, setError('vehicle', error.message));
              return of([]);
            })
          );
        })
      )
    ),

    hydrateVehicle: rxMethod<{year: string, make?: string, model?: string}>(
      pipe(
        tap(() => patchState(store, setPending('vehicle'))),
        switchMap(({year, make, model}) =>
          lookupVehicleService.getMakes(year).pipe(
            tap((makes) => {
              patchState(store, {
                vehicle: {...store.vehicle(), selected_year: year, makes}
              });
            }),
            switchMap((makes) => {
              const matchedMake = make ? makes?.find((m) => m.toLowerCase() === make.toLowerCase()) : undefined;

              if (!matchedMake) {
                patchState(store, setFulfilled('vehicle'));
                return of(null);
              }

              return lookupVehicleService.getModels(year, matchedMake).pipe(
                tap((models) => {
                  patchState(store, {
                    vehicle: {...store.vehicle(), selected_make: matchedMake, models}
                  });
                }),
                switchMap((models) => {
                  const matchedModel = model ? models?.find((m) => m.toLowerCase() === model.toLowerCase()) : undefined;

                  if (!matchedModel) {
                    patchState(store, setFulfilled('vehicle'));
                    return of(null);
                  }

                  return lookupVehicleService.getSubmodels(year, matchedMake, matchedModel).pipe(
                    tap((submodels) => {
                      patchState(store, setFulfilled('vehicle'), {
                        vehicle: {...store.vehicle(), selected_model: matchedModel, submodels}
                      });
                    })
                  );
                })
              );
            })
          )
        ),
        catchError((error) => {
          patchState(store, setError('vehicle', error.message));
          return of(null);
        })
      )
    ),

    setFitment(fitment: IVehicleFitment) {
      if (fitment.fg_chassis_id) {
        fetchAlternatesStep$(fitment.fg_chassis_id).subscribe();
      }

      patchState(store, {vehicle: {...store.vehicle(), selected_fitment: fitment}});
    },

    setRawsize(rawsize: string) {
      patchState(store, {vehicle: {...store.vehicle(), selected_rawsize: rawsize.replace(/[^0-9]/g, '')}});
    },

    resetVehicleLookup() {
      patchState(store, setIdle('vehicle'), setIdle('alternates'), {vehicle: initialVehicleState});
    },

    resetSizeLookup() {
      patchState(store, setIdle('size'), {size: initialSizeState});
    },

    reset() {
      patchState(store, setIdle('vehicle'), setIdle('size'), setIdle('alternates'), {vehicle: initialVehicleState, size: initialSizeState});
    },

    loadWidths: rxMethod<'retail' | 'commercial' | null>(
      pipe(
        tap((category) => {
          patchState(store, setPending('size'), {size: {...initialSizeState, selected_category: category}});
        }),
        switchMap((category) =>
          lookupSizeService.getWidths(category).pipe(
            tap((widths: string[]) => {
              patchState(store, setFulfilled('size'), {size: {...store.size(), widths}});
            }),
            catchError((error) => {
              patchState(store, setError('size', error.message));
              return of([]);
            })
          )
        )
      )
    ),

    setWidth: rxMethod<string>(
      pipe(
        tap((width: string) => {
          patchState(store, setPending('size'), {
            size: {
              ...store.size(),
              selected_width: width,
              ratios: null,
              selected_ratio: null,
              diameters: null,
              selected_diameter: null
            }
          });
        }),
        switchMap((width) => {
          return lookupSizeService.getRatios(width, store.size.selected_category()).pipe(
            tap((ratios: string[]) => {
              patchState(store, setFulfilled('size'), {size: {...store.size(), ratios}});
            }),
            catchError((error) => {
              patchState(store, setError('size', error.message));
              return of([]);
            })
          );
        })
      )
    ),

    setRatio: rxMethod<string>(
      pipe(
        tap((ratio: string) => {
          patchState(store, setPending('size'), {
            size: {...store.size(), selected_ratio: ratio, diameters: null, selected_diameter: null}
          });
        }),
        switchMap((ratio) => {
          const width = store.size.selected_width();
          const category = store.size.selected_category();
          return lookupSizeService.getRimDiameters(width!, ratio, category).pipe(
            tap((diameters: string[]) => {
              patchState(store, setFulfilled('size'), {size: {...store.size(), diameters}});
            }),
            catchError((error) => {
              patchState(store, setError('size', error.message));
              return of([]);
            })
          );
        })
      )
    ),

    setDiameter(diameter: string) {
      patchState(store, {size: {...store.size(), selected_diameter: diameter}});
    },

    hydrateSize: rxMethod<string>(
      pipe(
        tap(() => {
          patchState(store, setPending('size'), {size: {...initialSizeState, selected_category: store.size.selected_category()}});
        }),
        switchMap((rawsize) => {
          const digits = rawsize.replace(/[^0-9]/g, '');
          const category = store.size.selected_category();

          return lookupSizeService.getWidths(category).pipe(
            switchMap((widths) => {
              const width = findLongestPrefixMatch(widths, digits);
              if (!width) {
                patchState(store, setFulfilled('size'), {size: {...store.size(), widths}});
                return of(null);
              }

              patchState(store, setPending('size'), {size: {...store.size(), widths, selected_width: width}});
              const afterWidth = digits.slice(width.length);

              return lookupSizeService.getRatios(width, category).pipe(
                switchMap((ratios) => {
                  const ratio = findLongestPrefixMatch(ratios, afterWidth, ratioDigits);
                  if (!ratio) {
                    patchState(store, setFulfilled('size'), {size: {...store.size(), ratios}});
                    return of(null);
                  }

                  patchState(store, setPending('size'), {size: {...store.size(), ratios, selected_ratio: ratio}});
                  const afterRatio = afterWidth.slice(ratioDigits(ratio).length);

                  return lookupSizeService.getRimDiameters(width, ratio, category).pipe(
                    tap((diameters) => {
                      const diameterMatch = diameters.find((d) => String(d) === afterRatio);
                      const selected_diameter = diameterMatch === undefined ? null : String(diameterMatch);
                      patchState(store, setFulfilled('size'), {size: {...store.size(), diameters, selected_diameter}});
                    }),
                    catchError((error) => {
                      patchState(store, setError('size', error.message));
                      return of(null);
                    })
                  );
                }),
                catchError((error) => {
                  patchState(store, setError('size', error.message));
                  return of(null);
                })
              );
            }),
            catchError((error) => {
              patchState(store, setError('size', error.message));
              return of(null);
            })
          );
        })
      )
    )
  };
  })
);
